/* eslint-disable max-len */
/**
 * @fileoverview Refactored Post History List.
 *               PATH: src/components/PostHistoryList.js
 *               FIXED: Dark Theme & Strict Success Checks
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { collection, query, where, orderBy, limit, onSnapshot, doc, deleteDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from '../../firebaseConfig';
import { FiLoader, FiActivity, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { useToast } from '../shared/contexts/ToastContext';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';
import DashboardPostCard from '../common/DashboardPostCard';

function PostHistoryList({ userId, projectId, onRetryPost, recurringCampaigns = [] }) {
    const { addToast } = useToast();
    const { confirm } = useConfirmDialog();
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // UI State
    const [activeTab, setActiveTab] = useState('posted'); // 'posted' | 'failed'
    const [campaignFilter, setCampaignFilter] = useState('all');

    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    // --- 1. Data Fetching ---
    useEffect(() => {
        if (!userId || !projectId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const postsRef = collection(db, "projects", projectId, "posts");
        
        const qHistory = query(
            postsRef,
            where("userId", "==", userId),
            where("status", "in", ["posted", "failed", "cancelled"]),
            orderBy("updatedAt", "desc"),
            limit(50) 
        );

        const unsubscribe = onSnapshot(qHistory, (snapshot) => {
            if (isMountedRef.current) {
                const posts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setHistory(posts);
                setLoading(false);
            }
        }, (err) => {
            if (isMountedRef.current) {
                setError("Failed to load production history.");
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, [userId, projectId]);

    // --- 2. Logic: Strict Success Check ---
    const checkIsFullyPublished = useCallback((post) => {
        if (post.status !== 'posted') return false;
        
        const isMulti = typeof post.content === 'object' && post.content !== null;
        const targetPlatforms = isMulti ? Object.keys(post.content) : [post.platform?.toLowerCase() || 'facebook'];
        const successIds = post.platformPostId || "";

        // Check if every targeted platform has a success ID record
        return targetPlatforms.every(platform => successIds.includes(`${platform}:`));
    }, []);

    // --- 4. Action Handlers ---
    const handleDeleteRecord = async (post) => {
        const isPublished = post.status === 'posted' && post.platformPostId;

        if (isPublished) {
            // Published post — delete from platforms + Firestore
            const platforms = [];
            if (post.platformPostId.includes('facebook:')) platforms.push('Facebook');
            if (post.platformPostId.includes('instagram:')) platforms.push('Instagram');
            const platformNames = platforms.join(' and ') || 'social media';

            const confirmed = await confirm({
                title: 'Delete from ' + platformNames,
                message: `This will permanently delete the post from ${platformNames}. This action cannot be undone. Are you sure?`,
                confirmLabel: 'Delete Permanently',
                variant: 'danger',
            });
            if (!confirmed) return;
            try {
                const deletePublishedPost = httpsCallable(functions, 'deletePublishedPost');
                const result = await deletePublishedPost({ postId: post.id, projectId, confirmDelete: true });
                const data = result.data;
                addToast(data.message || "Post deleted.", "success");
                if (data.warnings?.length > 0) {
                    addToast(data.warnings.join('; '), "warning");
                }
            } catch (err) {
                addToast("Failed to delete post. Please try again.", "error");
            }
        } else {
            // Failed/cancelled record — just remove from Firestore
            const confirmed = await confirm({ title: 'Remove Record', message: 'Remove this post from history?', confirmLabel: 'Remove', variant: 'danger' });
            if (!confirmed) return;
            try {
                await deleteDoc(doc(db, "projects", projectId, "posts", post.id));
                addToast("Record removed.", "info");
            } catch (err) {
                addToast("Failed to remove record.", "error");
            }
        }
    };

    // --- 4. Filtering & Counters ---
    const displayedPosts = useMemo(() => {
        return history.filter(post => {
            // A. Campaign Filter
            if (campaignFilter !== 'all') {
                const matchesCampaign = campaignFilter === 'single' ? !post.streamId : post.streamId === campaignFilter;
                if (!matchesCampaign) return false;
            }

            // Logic for Cancelled Posts in History
            if (post.status === 'cancelled') {
                const scheduledTime = post.scheduledTime?.toDate ? post.scheduledTime.toDate() : new Date();
                const now = new Date();
                // Show in 'failed' (Issues) ONLY if it's in the past
                if (scheduledTime <= now && activeTab === 'failed') return true;
                return false;
            }

            // B. Tab Logic
            const isFullyPublished = checkIsFullyPublished(post);
            if (activeTab === 'posted') {
                return isFullyPublished;
            } else {
                return post.status === 'failed' || (post.status === 'posted' && !isFullyPublished);
            }
        }).sort((a, b) => {
            const dateA = a.postedAt?.toDate ? a.postedAt.toDate() : (a.updatedAt?.toDate ? a.updatedAt.toDate() : new Date(0));
            const dateB = b.postedAt?.toDate ? b.postedAt.toDate() : (b.updatedAt?.toDate ? b.updatedAt.toDate() : new Date(0));
            return dateB - dateA;
        });
    }, [history, activeTab, campaignFilter, checkIsFullyPublished]);


    // Calculate synchronized counts for tab headers
    const fullSuccessCount = useMemo(() => history.filter(p => checkIsFullyPublished(p)).length, [history, checkIsFullyPublished]);
    
    const totalIssuesCount = useMemo(() => {
        return history.filter(post => {
            if (post.status === 'cancelled') {
                const scheduledTime = post.scheduledTime?.toDate ? post.scheduledTime.toDate() : new Date();
                return scheduledTime <= new Date(); // Only count PAST cancelled posts
            }
            if (post.status === 'failed') return true;
            if (post.status === 'posted' && !checkIsFullyPublished(post)) return true;
            return false;
        }).length;
    }, [history, checkIsFullyPublished]);

    return (
        <div className="h-full flex flex-col">
             
             {/* Header: Status Tabs & Campaign Filter */}
             <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <nav className="flex space-x-2 bg-warm-dark/30 p-1.5 rounded-2xl border border-white/5" aria-label="History Tabs">
                    <button
                        onClick={() => setActiveTab('posted')}
                        className={`py-2 px-6 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center ${activeTab === 'posted' ? 'bg-warm-gray/40 text-pastel-green shadow-glow' : 'text-cream/40 hover:text-cream hover:bg-white/5'}`}
                    >
                        <FiCheckCircle className="mr-2"/> Published ({fullSuccessCount})
                    </button>
                    <button
                        onClick={() => setActiveTab('failed')}
                        className={`py-2 px-6 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center ${activeTab === 'failed' ? 'bg-warm-gray/40 text-soft-coral shadow-glow' : 'text-cream/40 hover:text-cream hover:bg-white/5'}`}
                    >
                        <FiAlertTriangle className="mr-2"/> 
                        Failed ({totalIssuesCount})
                    </button>
                </nav>

                <select
                    value={campaignFilter}
                    onChange={(e) => setCampaignFilter(e.target.value)}
                    disabled={loading}
                    className="form-select text-xs py-2.5 w-full sm:w-auto font-bold rounded-xl bg-warm-gray/20 text-cream border-transparent focus:border-slate-blue/50"
                >
                    <option value="all">All Campaigns</option>
                    <option value="single">Single Posts</option>
                    {recurringCampaigns && recurringCampaigns.map(campaign => (
                        <option key={campaign.id} value={campaign.id}>{campaign.streamName}</option>
                    ))}
                </select>
            </div>

            {/* Content Area */}
            <div className="flex-1">
                {loading ? (
                    <div className="text-center py-20">
                        <FiLoader className="animate-spin inline-block text-3xl text-slate-blue mb-4"/>
                        <p className="text-cream/40 text-xs font-bold tracking-widest uppercase">Syncing Archive...</p>
                    </div>
                ) : error ? (
                    <p className="text-soft-coral text-sm bg-soft-coral/10 p-4 rounded-xl border border-soft-coral/20">{error}</p>
                ) : displayedPosts.length === 0 ? (
                    <div className="text-center py-24 bg-warm-dark/30 rounded-3xl border-2 border-dashed border-white/5">
                        <FiActivity className="mx-auto h-12 w-12 text-cream/10 mb-4" />
                        <p className="text-cream/40 font-bold uppercase tracking-wide">
                            {activeTab === 'posted' ? "No full successes logged." : "No production issues found."}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-6 max-h-[70vh] overflow-y-auto pr-2 custom-scrollbar">
                        {displayedPosts.map((post) => (
                            <DashboardPostCard
                                key={post.id}
                                post={post}
                                viewMode="history"
                                onRetry={(p, platform) => onRetryPost(p, platform)}
                                onEdit={(p, platform) => onRetryPost(p, platform)}
                                onDelete={handleDeleteRecord}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

PostHistoryList.propTypes = {
    userId: PropTypes.string,
    projectId: PropTypes.string.isRequired,
    onRetryPost: PropTypes.func,
    recurringCampaigns: PropTypes.array,
};

export default PostHistoryList;
